import type { PromotionChannel, PromotionWithTargets } from '../domain/promotionTypes';
import { PROMOTION_KIND_LABELS, PROMOTION_SCOPE_LABELS } from './promotionPresentation';

export type PromotionConflict = {
  promotionA: PromotionWithTargets;
  promotionB: PromotionWithTargets;
  reason: string;
};

function dateRangesOverlap(
  aStart: string | null | undefined,
  aEnd: string | null | undefined,
  bStart: string | null | undefined,
  bEnd: string | null | undefined
): boolean {
  const aS = aStart ? Date.parse(aStart) : 0;
  const aE = aEnd ? Date.parse(aEnd) : Infinity;
  const bS = bStart ? Date.parse(bStart) : 0;
  const bE = bEnd ? Date.parse(bEnd) : Infinity;
  return aS <= bE && bS <= aE;
}

function branchesOverlap(
  aBranches: string[] | null | undefined,
  bBranches: string[] | null | undefined
): boolean {
  if (!aBranches || aBranches.length === 0) return true;
  if (!bBranches || bBranches.length === 0) return true;
  return aBranches.some((b) => bBranches.includes(b));
}

function targetsOverlap(
  aTargets: PromotionWithTargets['targets'],
  bTargets: PromotionWithTargets['targets']
): boolean {
  if (aTargets.length === 0 || bTargets.length === 0) return true;
  return aTargets.some((at) =>
    bTargets.some((bt) => at.targetType === bt.targetType && at.targetId === bt.targetId)
  );
}

function channelsOverlap(
  aChannels: PromotionChannel[] | null | undefined,
  bChannels: PromotionChannel[] | null | undefined
): boolean {
  // Ausente/vacío = ambos canales (paridad legacy).
  if (!aChannels || aChannels.length === 0) return true;
  if (!bChannels || bChannels.length === 0) return true;
  return aChannels.some((c) => bChannels.includes(c));
}

export type PromotionConflictOptions = {
  /** Punto de evaluación de la ventana de vigencia (default: ahora). */
  now?: number;
  /**
   * `true` (default): solo promos que compiten HOY — excluye programadas
   * (startsAt futuro) además de las vencidas. `false`: incluye programadas —
   * útil para avisar en el form que la promo va a pisar a otra cuando arranque.
   * Las vencidas (endsAt pasado) se excluyen siempre: ya no compiten nunca más.
   */
  liveOnly?: boolean;
};

function competesInWindow(
  p: Pick<PromotionWithTargets, 'startsAt' | 'endsAt'>,
  now: number,
  liveOnly: boolean
): boolean {
  if (p.endsAt) {
    const endMs = Date.parse(p.endsAt);
    if (!Number.isNaN(endMs) && endMs < now) return false;
  }
  if (liveOnly && p.startsAt) {
    const startMs = Date.parse(p.startsAt);
    if (!Number.isNaN(startMs) && startMs > now) return false;
  }
  return true;
}

export function detectPromotionConflicts(
  promotions: PromotionWithTargets[],
  options?: PromotionConflictOptions
): PromotionConflict[] {
  const now = options?.now ?? Date.now();
  const liveOnly = options?.liveOnly !== false;
  const active = promotions.filter(
    (p) => p.isActive && !p.deletedAt && competesInWindow(p, now, liveOnly)
  );
  const conflicts: PromotionConflict[] = [];

  for (let i = 0; i < active.length; i++) {
    for (let j = i + 1; j < active.length; j++) {
      const a = active[i]!;
      const b = active[j]!;

      if (a.applyMode !== 'automatic' || b.applyMode !== 'automatic') continue;

      if (a.stackable || b.stackable) continue;

      if (!dateRangesOverlap(a.startsAt, a.endsAt, b.startsAt, b.endsAt)) continue;

      if (!branchesOverlap(a.branchIds, b.branchIds)) continue;

      if (!channelsOverlap(a.channels, b.channels)) continue;

      if (!targetsOverlap(a.targets, b.targets)) continue;

      const reasons: string[] = ['no se suman'];
      if (a.kind === b.kind && a.scope === b.scope) {
        reasons.push(`mismo tipo (${PROMOTION_KIND_LABELS[a.kind]} / ${PROMOTION_SCOPE_LABELS[a.scope]})`);
      }

      conflicts.push({
        promotionA: a,
        promotionB: b,
        reason: reasons.join(', ') || 'mismas fechas y productos',
      });
    }
  }

  return conflicts;
}
